import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, message } from 'antd';

const { Option } = Select;

const EditTaskModal = ({ visible, onSave, onCancel, task, materials, confirmLoading = false }) => {
  const [form] = Form.useForm();
  const [formChanged, setFormChanged] = useState(false);
  const [materialStatus, setMaterialStatus] = useState('');

  
  useEffect(() => {
    if (visible && task) {
      
      form.resetFields();
      
      
      form.setFieldsValue({
        jobNumber: task.jobNumber || '',
        customer: task.customer || '',
        material: task.material !== "-" ? task.material : undefined,
        materialQuantity: task.materialQuantity !== "-" ? task.materialQuantity : undefined,
        materialStatus: task.materialStatus !== "-" ? task.materialStatus : undefined,
        machine: task.machine !== "-" ? task.machine : undefined,
        jobStatus: task.jobStatus !== "-" ? task.jobStatus : undefined
      });
      
      
      setMaterialStatus(task.materialStatus);
      
      
      setFormChanged(false);
    }
  }, [visible, task, form]);

  const handleFormChange = (changedValues, allValues) => {
    
    setFormChanged(true);
    
    
    if ('materialStatus' in changedValues) {
      setMaterialStatus(changedValues.materialStatus);
    }
    
    
    if (changedValues.jobStatus === 'red' && allValues.materialStatus === 'no stock') {
      message.error('Cannot start job: material is not in stock');
      form.setFieldsValue({ jobStatus: 'yellow' });
      return;
    }
    
    
    if (changedValues.materialStatus === 'no stock' && allValues.jobStatus === 'red') {
      form.setFieldsValue({ jobStatus: 'yellow' });
      message.warning('Job status changed to waiting: material is not in stock');
    }
  };

  const handleOk = () => {
    form.validateFields()
      .then(values => {
        
        const formattedValues = {
          ...values,
          material: values.material ?? "-",
          materialQuantity: values.materialQuantity ?? "-",
          materialStatus: values.materialStatus ?? "-",
          machine: values.machine ?? "-",
          jobStatus: values.jobStatus ?? "-"
        };
        
        
        if (formattedValues.jobStatus === 'red' && formattedValues.materialStatus === 'no stock') {
          message.error('Cannot start job: material is not in stock');
          return;
        }
        
        onSave({ ...task, ...formattedValues });
        setFormChanged(false);
      })
      .catch(info => {
        console.error('Validation failed:', info);
        message.error('Please check your input');
      });
  };

  const handleCancel = () => {
    
    if (formChanged) {
      
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to cancel?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          form.resetFields();
          setFormChanged(false);
          onCancel();
        }
      });
    } else {
      
      form.resetFields();
      onCancel();
    }
  };

  
  const isStartedDisabled = materialStatus === 'no stock';

  return (
    <Modal
      title="Edit Task"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Save"
      cancelText="Cancel"
      destroyOnClose={true}
      confirmLoading={confirmLoading}
    >
      <Form
        form={form}
        layout="vertical"
        name="edit_task_form"
        onValuesChange={handleFormChange}
        preserve={false}
      >
        <Form.Item
          name="jobNumber"
          label="Job Number"
          rules={[{ required: true, message: 'Please input the job number' }]}
        >
          <Input placeholder="Enter job number" />
        </Form.Item>

        <Form.Item
          name="customer"
          label="Customer"
          rules={[{ required: true, message: 'Please input the customer name' }]}
        >
          <Input placeholder="Enter customer name" />
        </Form.Item>

        <Form.Item
          name="material"
          label="Material"
        >
          <Select placeholder="Select material" allowClear>
            {materials.map(material => (
              <Option key={material.id} value={material.id}>
                {material.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="materialQuantity"
          label="Material Quantity"
        >
          <InputNumber min={0} placeholder="0" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="materialStatus"
          label="Material Status"
        >
          <Select 
            placeholder="Select material status" 
            allowClear
          >
            <Option value="in stock">In Stock</Option>
            <Option value="no stock">No Stock</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="machine"
          label="Machine"
        >
          <Select placeholder="Select machine" allowClear>
            <Option value="3KW">3KW</Option>
            <Option value="12KW">12KW</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="jobStatus"
          label="Job Status"
        >
          <Select 
            placeholder="Select job status" 
            allowClear
          >
            <Option value="yellow">Waiting</Option>
            <Option value="red" disabled={isStartedDisabled}>
              Started {isStartedDisabled && "(Material not in stock)"}
            </Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditTaskModal;